/**
 * utils/supportCaseVocabulary.js
 * ---------------------------------------------------------------------------
 * ONE vocabulary for support cases across the whole web client.
 *
 * A support case is how a customer reports an issue and follows it to an
 * answer. The server owns every rule: who may see a case, what it may become
 * next, what its targets are. This module owns only the WORDS, plus the few
 * pure decisions a screen has to make before it can draw anything.
 *
 * Two things live here on purpose:
 *
 *   1. The human labels. The raw enums (OPEN, PENDING_CUSTOMER, MISSING_ITEM)
 *      are storage values, not English. Every screen reads them from here so
 *      the list, the detail and the report form can never drift, and so no
 *      screen ever prints a shouting enum at a customer.
 *   2. A shipped FALLBACK of the vocabulary. The live lists come from
 *      GET /api/support-cases/meta/options, which is the source of truth, but
 *      a customer with a bad connection still has to be able to report an
 *      issue. So the fallback is what the form falls back to, never what it
 *      prefers.
 *
 * Framework free, like utils/minimumOrder.js, so the api module, the screens
 * and the tests can all import it.
 */

/* The eight case statuses, in the order a case moves through them, with the
   exact customer-facing wording from the build contract. PENDING_CUSTOMER is
   the one that matters most: it means WE are waiting on the customer, so it
   has to read as an ask, not as a state. */
export const CASE_STATUS_LABELS = {
  OPEN: "Open",
  ACKNOWLEDGED: "Acknowledged",
  IN_PROGRESS: "In progress",
  PENDING_CUSTOMER: "Waiting on you",
  ESCALATED: "Escalated",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
};

export const CASE_STATUSES = Object.keys(CASE_STATUS_LABELS);

/* Chip colours, kept beside the labels so a status can never be worded one way
   and coloured another. Tones only: the components own the palette. */
export const CASE_STATUS_TONES = {
  OPEN: "info",
  ACKNOWLEDGED: "info",
  IN_PROGRESS: "info",
  PENDING_CUSTOMER: "warn",
  ESCALATED: "warn",
  RESOLVED: "good",
  CLOSED: "muted",
  CANCELLED: "muted",
};

/* The shipped fallback category list. Same values and same order as the
   server enum, worded as a question a customer would recognise rather than as
   an internal routing label. */
export const FALLBACK_CASE_CATEGORIES = [
  { value: "ORDER_ISSUE", label: "Problem with an order" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "PAYMENT_REFUND", label: "Payment or refund" },
  { value: "PRODUCT_QUALITY", label: "Product quality" },
  { value: "MISSING_ITEM", label: "Missing item" },
  { value: "WRONG_ITEM", label: "Wrong item" },
  { value: "VENDOR_SERVICE", label: "Vendor service" },
  { value: "RIDER_CONDUCT", label: "Rider conduct" },
  { value: "APP_TECHNICAL", label: "App or website problem" },
  { value: "ACCOUNT", label: "My account" },
  { value: "OTHER", label: "Something else" },
];

export const CASE_PRIORITY_LABELS = {
  LOW: "Low",
  NORMAL: "Normal",
  HIGH: "High",
  URGENT: "Urgent",
};

/* Outcomes a customer may be shown on a resolved case. The money side of a
   resolution (resolution.financialImpact) is redacted by the server and is
   never rendered here either, so these are wording only. */
export const CASE_RESOLUTION_LABELS = {
  RESOLVED_NO_ACTION: "Resolved",
  REFUND_ISSUED: "Refund issued",
  PARTIAL_REFUND: "Partial refund issued",
  REPLACEMENT_SENT: "Replacement sent",
  CREDIT_ISSUED: "Credit issued",
  ESCALATED_EXTERNAL: "Escalated outside Lusuku",
  DUPLICATE: "Duplicate of another case",
  INVALID: "No issue found",
};

/* Who wrote a message. A customer sees their own name as "You" and everyone
   on our side as one voice, because which desk answered is our business. */
export const CASE_AUTHOR_LABELS = {
  CUSTOMER: "You",
  VENDOR: "The vendor",
  VENDOR_MANAGER: "The vendor",
  STAFF: "Lusuku support",
  RIDER: "The rider",
};

/* The contract's own limits, so the form and the tests read the same numbers
   the server validates against. */
export const SUBJECT_MAX = 140;
export const DESCRIPTION_MAX = 4000;

const upper = (value) => String(value ?? "").trim().toUpperCase();

/** Human label for a status. An unknown status prints as itself, never blank. */
export const caseStatusLabel = (status) =>
  CASE_STATUS_LABELS[upper(status)] || String(status ?? "");

/** Chip tone for a status. Unknown statuses read as neutral, not as an alarm. */
export const caseStatusTone = (status) => CASE_STATUS_TONES[upper(status)] || "muted";

export const casePriorityLabel = (priority) =>
  CASE_PRIORITY_LABELS[upper(priority)] || String(priority ?? "");

export const caseResolutionLabel = (outcome) =>
  CASE_RESOLUTION_LABELS[upper(outcome)] || String(outcome ?? "");

export const caseAuthorLabel = (role) =>
  CASE_AUTHOR_LABELS[upper(role)] || "Lusuku support";

/**
 * Label for one category, against whichever list is in hand: the live one from
 * meta/options when it loaded, the shipped fallback when it did not.
 */
export const caseCategoryLabel = (value, categories = FALLBACK_CASE_CATEGORIES) => {
  const key = upper(value);
  const list = Array.isArray(categories) ? categories : [];
  const hit = list.find((entry) => upper(entry?.value) === key);
  if (hit?.label) return hit.label;
  const shipped = FALLBACK_CASE_CATEGORIES.find((entry) => entry.value === key);
  return shipped ? shipped.label : String(value ?? "");
};

/**
 * One option list, normalised. GET /meta/options is free to answer with plain
 * enum strings or with { value, label } pairs, and a client that only accepts
 * one of those shapes breaks the day the other arrives. Anything unusable
 * returns an empty list so the caller can fall back rather than render junk.
 */
export const normalizeOptionList = (raw, labelOf = (value) => String(value)) => {
  if (!Array.isArray(raw)) return [];
  return raw.reduce((out, entry) => {
    if (typeof entry === "string") {
      const value = upper(entry);
      if (value) out.push({ value, label: labelOf(value) });
      return out;
    }
    const value = upper(entry?.value ?? entry?.key ?? entry?.code ?? entry?.name);
    if (!value) return out;
    out.push({ value, label: entry?.label || entry?.title || labelOf(value) });
    return out;
  }, []);
};

/**
 * The whole vocabulary from a meta/options response, with the shipped list
 * standing in for anything the server did not send. Tolerant of the envelope,
 * because the only promise in the contract is that the three vocabularies are
 * in there somewhere.
 */
export const normalizeCaseOptions = (payload) => {
  const body = payload?.options || payload?.data || payload || {};
  const categories = normalizeOptionList(body.categories, (value) =>
    caseCategoryLabel(value)
  );
  const statuses = normalizeOptionList(body.statuses, caseStatusLabel);
  const priorities = normalizeOptionList(body.priorities, casePriorityLabel);
  return {
    categories: categories.length ? categories : FALLBACK_CASE_CATEGORIES,
    statuses: statuses.length
      ? statuses
      : CASE_STATUSES.map((value) => ({ value, label: caseStatusLabel(value) })),
    priorities: priorities.length
      ? priorities
      : Object.keys(CASE_PRIORITY_LABELS).map((value) => ({
          value,
          label: casePriorityLabel(value),
        })),
    /* True only when the lists above actually came off the wire, so a screen
       can say so rather than pretending. */
    live: categories.length > 0,
  };
};

/**
 * The rows out of a list response, whatever the envelope calls them. The
 * server owns the scoping, so whatever comes back is already this customer's
 * own cases; this only has to find the array.
 */
export const readCaseList = (payload) => {
  const body = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
  if (Array.isArray(body)) return body;
  const candidates = [body?.cases, body?.supportCases, body?.items, body?.results, body?.data];
  const hit = candidates.find((value) => Array.isArray(value));
  return hit || [];
};

/** The single case out of a detail or mutation response. */
export const readCase = (payload) => {
  const body = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
  const candidates = [body?.case, body?.supportCase, body?.item, body?.data];
  const hit = candidates.find((value) => value && typeof value === "object" && !Array.isArray(value));
  if (hit) return hit;
  return body && typeof body === "object" && (body._id || body.caseNumber) ? body : null;
};

/** Page metadata from a list response, defaulted so a caller can always read it. */
export const readCasePagination = (payload, fallbackCount = 0) => {
  const body = payload?.data && !Array.isArray(payload.data) ? payload.data : payload || {};
  const meta = body?.pagination || body?.meta || body || {};
  const num = (value, or) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : or;
  };
  const total = num(meta.total ?? meta.totalCount ?? meta.count, fallbackCount);
  const limit = num(meta.limit ?? meta.pageSize, 20);
  const page = num(meta.page ?? meta.currentPage, 1);
  const pages = num(meta.pages ?? meta.totalPages, Math.max(1, Math.ceil(total / limit)));
  return { page, limit, total, pages, hasMore: page < pages };
};

/**
 * What a customer may do next with a case, so the buttons and the list chip
 * never disagree. The server is still the gate; this only decides what is
 * worth OFFERING.
 */
export const isCaseClosed = (status) => ["CLOSED", "CANCELLED"].includes(upper(status));
export const canRateCase = (caseDoc) =>
  ["RESOLVED", "CLOSED"].includes(upper(caseDoc?.status)) && !caseDoc?.satisfaction?.rating;
export const canReopenCase = (caseDoc) => ["RESOLVED", "CLOSED"].includes(upper(caseDoc?.status));
export const canMessageCase = (caseDoc) => !isCaseClosed(caseDoc?.status);
export const canEscalateCase = (caseDoc) =>
  ["OPEN", "ACKNOWLEDGED", "IN_PROGRESS", "PENDING_CUSTOMER"].includes(upper(caseDoc?.status));

/**
 * The messages a customer is allowed to see.
 *
 * The server already strips internal notes. This strips them again, because a
 * UI that renders whatever arrives is one server bug away from showing a
 * customer what we said about them internally. Belt and braces, deliberately.
 */
export const visibleMessages = (messages) =>
  (Array.isArray(messages) ? messages : []).filter((message) => message?.isInternal !== true);

/**
 * A case reduced to exactly the fields a customer screen draws.
 *
 * Anything not listed here is dropped rather than carried into component
 * state: internal notes, who assigned what, who escalated, tags, and the
 * money on a resolution. The server redacts them for a customer; this makes
 * sure a screen cannot render them even if one slips through.
 */
export const forCustomerDisplay = (caseDoc) => {
  if (!caseDoc || typeof caseDoc !== "object") return null;
  const sla = caseDoc.sla || {};
  const resolution = caseDoc.resolution || {};
  return {
    _id: caseDoc._id ?? null,
    caseNumber: caseDoc.caseNumber || "",
    subject: caseDoc.subject || "",
    description: caseDoc.description || "",
    category: caseDoc.category || "",
    status: upper(caseDoc.status) || "OPEN",
    priority: upper(caseDoc.priority) || "",
    currency: caseDoc.currency || "",
    createdAt: caseDoc.createdAt || null,
    lastActivityAt: caseDoc.lastActivityAt || caseDoc.updatedAt || caseDoc.createdAt || null,
    order: {
      id: caseDoc.order?.id ?? null,
      number: caseDoc.order?.number || "",
      placedAt: caseDoc.order?.placedAt || null,
    },
    vendor: { name: caseDoc.vendor?.name || "" },
    messages: visibleMessages(caseDoc.messages).map((message) => ({
      authorRole: upper(message.authorRole),
      authorName: message.authorName || "",
      body: message.body || "",
      attachments: (Array.isArray(message.attachments) ? message.attachments : [])
        .filter((file) => file?.url)
        .map((file) => ({ url: file.url, type: file.type || "", name: file.name || "" })),
      createdAt: message.createdAt || null,
    })),
    evidence: (Array.isArray(caseDoc.evidence) ? caseDoc.evidence : [])
      .filter((item) => item?.url)
      .map((item) => ({
        url: item.url,
        type: item.type || "",
        caption: item.caption || "",
        uploadedAt: item.uploadedAt || null,
      })),
    statusHistory: (Array.isArray(caseDoc.statusHistory) ? caseDoc.statusHistory : []).map(
      (entry) => ({
        from: upper(entry?.from),
        to: upper(entry?.to),
        at: entry?.at || null,
        note: entry?.note || "",
      })
    ),
    sla: {
      firstResponseDueAt: sla.firstResponseDueAt || null,
      resolutionDueAt: sla.resolutionDueAt || null,
      firstRespondedAt: sla.firstRespondedAt || null,
      resolvedAt: sla.resolvedAt || null,
      breachedFirstResponse: sla.breachedFirstResponse === true,
      breachedResolution: sla.breachedResolution === true,
    },
    /* Level only. Who escalated it is an internal detail. */
    escalationLevel: Number(caseDoc.escalation?.level) || 0,
    resolution: resolution.outcome || resolution.summary || resolution.resolvedAt
      ? {
          outcome: upper(resolution.outcome),
          summary: resolution.summary || "",
          resolvedAt: resolution.resolvedAt || null,
        }
      : null,
    satisfaction: caseDoc.satisfaction?.rating
      ? {
          rating: Number(caseDoc.satisfaction.rating) || 0,
          comment: caseDoc.satisfaction.comment || "",
          ratedAt: caseDoc.satisfaction.ratedAt || null,
        }
      : null,
  };
};

/**
 * What to tell the customer about timing, in one sentence.
 *
 * Only ever a promise or an apology, never a number of minutes of internal
 * target: before the first reply it is when to expect one, after it is the
 * resolution target, and once resolved there is nothing to promise.
 */
export const describeCaseSla = (caseDoc, now = new Date()) => {
  const status = upper(caseDoc?.status);
  const sla = caseDoc?.sla || {};
  const at = (value) => {
    const d = value ? new Date(value) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  };
  const clock = at(now) || new Date();

  if (["RESOLVED", "CLOSED"].includes(status)) {
    return { tone: "good", label: "Resolved", dueAt: at(sla.resolvedAt) };
  }
  if (status === "CANCELLED") return { tone: "muted", label: "Cancelled", dueAt: null };

  const firstResponded = at(sla.firstRespondedAt);
  if (!firstResponded) {
    const due = at(sla.firstResponseDueAt);
    if (sla.breachedFirstResponse || (due && due < clock)) {
      return { tone: "warn", label: "A reply is overdue, we are on it", dueAt: due };
    }
    return { tone: "info", label: "Waiting for our first reply", dueAt: due };
  }

  const due = at(sla.resolutionDueAt);
  if (sla.breachedResolution || (due && due < clock)) {
    return { tone: "warn", label: "This is taking longer than we aim for", dueAt: due };
  }
  return { tone: "info", label: "We are working on it", dueAt: due };
};

/**
 * Client side validation for the report form, matching the server's own
 * limits. Returns a field keyed error bag so the form can mark the field that
 * is wrong instead of printing one message at the top.
 */
export const validateCaseDraft = (draft = {}) => {
  const subject = String(draft.subject ?? "").trim();
  const description = String(draft.description ?? "").trim();
  const category = upper(draft.category);
  const errors = {};

  if (!subject) errors.subject = "Give your issue a short subject.";
  else if (subject.length > SUBJECT_MAX)
    errors.subject = `Keep the subject to ${SUBJECT_MAX} characters or fewer.`;

  if (!category) errors.category = "Pick what this is about.";

  if (!description) errors.description = "Tell us what happened.";
  else if (description.length > DESCRIPTION_MAX)
    errors.description = `Keep the description to ${DESCRIPTION_MAX} characters or fewer.`;

  return { valid: Object.keys(errors).length === 0, errors };
};

const EVIDENCE_TYPES = [
  { type: "image", extensions: ["jpg", "jpeg", "png", "gif", "webp", "heic", "bmp"] },
  { type: "video", extensions: ["mp4", "mov", "webm", "m4v", "avi"] },
  { type: "document", extensions: ["pdf", "doc", "docx", "csv", "xls", "xlsx", "txt"] },
];

/** Best guess at what an evidence URL points at, for the icon beside it. */
export const evidenceTypeOf = (url) => {
  const clean = String(url ?? "").split("?")[0].split("#")[0];
  const ext = clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
  const hit = EVIDENCE_TYPES.find((entry) => entry.extensions.includes(ext));
  return hit ? hit.type : "link";
};

/**
 * The optional evidence box, parsed. One URL per line, because asking a
 * customer for JSON would be absurd and asking for one URL only would lose
 * the second photo they already have.
 *
 * Returns both the payload the contract expects and the lines that were not
 * usable, so the form can point at them rather than silently dropping them.
 */
export const parseEvidenceUrls = (text) => {
  /* Split on line breaks and commas only, never on plain whitespace: a URL
     cannot contain a space, so a line with one in it is a line the customer
     needs told about as a whole, not three fragments of an error message. */
  const lines = String(text ?? "")
    .split(/[\n\r,]+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const evidence = [];
  const rejected = [];
  const seen = new Set();

  lines.forEach((line) => {
    if (!/^https?:\/\/\S+$/i.test(line)) {
      rejected.push(line);
      return;
    }
    if (seen.has(line)) return;
    seen.add(line);
    evidence.push({ url: line, type: evidenceTypeOf(line), caption: "" });
  });

  return { evidence, rejected };
};

/**
 * The order context for a case raised from an order detail screen.
 *
 * Kept here rather than inline on that screen because an order carries its
 * vendor in more than one place: the per-vendor split lives on
 * orderItems[].vendorId, and only some order shapes also carry a top level
 * one. The screen must not have to know that, and this way it can be tested.
 */
export const orderContextForCase = (order) => {
  if (!order || typeof order !== "object") {
    return { orderId: null, orderNumber: "", vendorId: null };
  }
  const idOf = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value === "object") return value._id ? String(value._id) : null;
    return null;
  };
  const items = Array.isArray(order.orderItems) ? order.orderItems : [];
  const fromItems = items.map((item) => idOf(item?.vendorId)).find(Boolean) || null;
  const orderId = idOf(order._id);
  return {
    orderId,
    orderNumber: order.orderNumber || orderId || "",
    vendorId: fromItems || idOf(order.vendorId) || idOf(order.vendor) || null,
  };
};

/**
 * The create payload for POST /api/support-cases, exactly the fields the
 * contract lists and no others.
 *
 * THE ORDER IS OPTIONAL, and that is the reason this lives here rather than
 * inline in the dialog. A case raised from an order detail screen and a case
 * raised from the account panel go through the SAME form, and the only
 * difference between them is that the second one has no context to attach.
 * `orderId` and `vendorId` are both optional on create, and the server routes
 * a case with neither to staff, so a case without an order is a complete case
 * and not a half filled one. An empty context therefore omits the keys rather
 * than sending nulls, which is what the server reads.
 *
 * @param {object} draft {subject, description, category, context, evidence}
 * @returns {object} the payload to post
 */
export const buildCasePayload = ({
  subject,
  description,
  category,
  context,
  evidence,
} = {}) => {
  const payload = {
    subject: String(subject ?? "").trim(),
    description: String(description ?? "").trim(),
    category: String(category ?? "").trim().toUpperCase(),
  };
  const orderId = context?.orderId ? String(context.orderId) : "";
  if (orderId) payload.orderId = orderId;
  const vendorId = context?.vendorId ? String(context.vendorId) : "";
  if (vendorId) payload.vendorId = vendorId;
  const rows = (Array.isArray(evidence) ? evidence : []).filter(
    (row) => row && row.url
  );
  if (rows.length) payload.evidence = rows;
  return payload;
};
